/**
 * App-wide selected location — the rail's location switcher writes it, the
 * visit pages read it as a query input. `null` = all locations. The Address
 * Book deliberately ignores it (visitors are customer-scoped).
 *
 * Persisted so the choice survives reloads (same idea as the old app's
 * `locationId` store2 key, but namespaced to this app).
 */

import { Injectable, signal } from '@angular/core';

export interface SelectedLocation {
  _id: string;
  name: string;
}

const STORAGE_KEY = 'rdx-visitors.location';

@Injectable({ providedIn: 'root' })
export class LocationState {
  private readonly _selected = signal<SelectedLocation | null>(read());

  /** The selected location, or null for "All locations". */
  readonly selected = this._selected.asReadonly();

  select(location: SelectedLocation | null): void {
    this._selected.set(location);
    try {
      if (location) localStorage.setItem(STORAGE_KEY, JSON.stringify(location));
      else localStorage.removeItem(STORAGE_KEY);
    } catch {
      // Storage unavailable (private mode) — selection still works in-memory.
    }
  }
}

function read(): SelectedLocation | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as SelectedLocation;
    return parsed && parsed._id && parsed.name ? parsed : null;
  } catch {
    return null;
  }
}
